import React from 'react';
import { withStyles } from '@lifeomic/chroma-react/styles';
import { Button } from '@lifeomic/chroma-react/components/Button';
import { compose } from 'recompose';
import { connect } from 'react-redux';
import { DotLoader } from '@lifeomic/chroma-react/components/DotLoader';
import { ChevronDown, CheckCircle } from '@lifeomic/chromicons';
import { defineMessages, FormattedMessage, injectIntl } from 'react-intl';
import { push } from 'connected-react-router';
import _ from 'lodash';
import urls from '../../util/urls';
import { withRouter, Link } from 'react-router-dom';
import {
  Popover,
  PopoverActions,
  PopoverList,
  PopoverItem,
} from '@lifeomic/chroma-react/components/Popover';
import {
  projectCohortPickerActions,
  projectCohortPickerSelectors,
} from '@lifeomic/phc-web-toolkit/src/redux/modules/projectCohortPicker';
import { getTestProps } from '@lifeomic/phc-web-toolkit/src/util/testProps';
import watchForProjectChange from '@lifeomic/phc-web-toolkit/src/components/common/watchForProjectChange';
import { accountSelectors } from '@lifeomic/phc-web-toolkit/src/redux/modules/account';
import { projectPickerSelectors } from '@lifeomic/phc-web-toolkit/src/redux/modules/projectPicker';
import { evaluatedPolicySelectors } from '@lifeomic/phc-web-toolkit/src/redux/modules/evaluatedPolicy';
import { featuresSelectors } from '@lifeomic/phc-web-toolkit/src/redux/modules/features';
import { Pill } from '@lifeomic/chroma-react/components/Pill';

const messages = defineMessages({
  createACohort: {
    id: 'cohort.cohortPicker.createACohort',
    defaultMessage: 'Create a cohort',
  },
  allCohorts: {
    id: 'cohort.cohortPicker.allCohorts',
    defaultMessage: 'All Cohorts',
  },
});

const styles = theme => ({
  completeIcon: {
    color: theme.palette.primary.main,
    marginLeft: theme.spacing(1),
    minHeight: 18,
    minWidth: 18,
  },
  popover: {
    zIndex: 1500,
  },
  popoverItem: {
    overflow: 'hidden',
    position: 'relative',
    width: '15.5rem',
    '& p': {
      width: 'calc(100% - 20px)',
    },
  },
  seeAllLink: {
    width: '100%',
    '&:hover': {
      textDecoration: 'none',
    },
  },
  cohortPickerCohortButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  chevron: {
    marginLeft: theme.spacing(0.5),
  },
  pill: {
    color: theme.palette.black[400],
    background: theme.palette.black[50],
    marginLeft: theme.spacing(1),
    lineHeight: 1,
  },
});

class ProjectCohortPicker extends React.PureComponent {
  state = {
    activeProject: '',
    activeProjectCohort: '',
  };

  componentDidMount() {
    if (
      this.props.activeAccount &&
      this.props.activeProject?.id &&
      (!_.get(this, 'props.cohort.id') ||
        _.get(this, 'props.cohorts', []).length === 0)
    ) {
      this.props.fetchProjectCohorts({
        params: {
          projectId: this.props.activeProject.id,
          fields: 'name,id,description,ownerProject,resultCount',
          pageSize: 1000,
        },
      });
    }
  }

  static getDerivedStateFromProps(props, state) {
    const currentActiveProjectId = _.get(state, 'activeProject.id');
    const nextActiveProjectId = _.get(props, 'activeProject.id');
    if (
      (currentActiveProjectId || nextActiveProjectId) &&
      currentActiveProjectId !== nextActiveProjectId
    ) {
      state.activeProject = props.activeProject;
      state.activeProjectCohort = props.activeProjectCohort;
      props.fetchProjectCohorts({
        params: {
          projectId: nextActiveProjectId,
        },
      });
    }
    return state;
  }

  componentDidUpdate(prevProps) {
    /**
     * Anytime the default changes that means a new project was selected or the cohort list was loaded
     */
    if (
      !this.props.isInitializingProjectCohorts &&
      prevProps?.defaultProjectCohortSelection?.id !==
        this.props.defaultProjectCohortSelection?.id
    ) {
      this.props.setActiveProjectCohort(
        this.props.defaultProjectCohortSelection?.id,
      );
    }
  }

  handleSelect = (id, popover) => () => {
    this.props.setActiveProjectCohort(id);
    popover.hide();
  };

  handleAddClick = e => {
    this.props.push(urls.app.cohorts());
  };

  handleSeeAllCohortsClick = popover => e => {
    popover.hide();
  };

  render() {
    const {
      classes,
      selectableProjectCohorts,
      activeProjectCohort,
      showLoader = true,
      intl: { formatMessage },
      isInitializingCohorts,
      isUserAccessRestrictedToCohorts,
    } = this.props;

    const hasMenuItems = selectableProjectCohorts.length > 0;

    if (isInitializingCohorts && showLoader) {
      return <DotLoader size={1} />;
    }

    return (
      <Popover
        className={classes.popover}
        aria-label={
          <FormattedMessage
            id="cohort.cohortPicker.cohortSwitcher"
            defaultMessage="Cohort switcher"
          />
        }
        placement="bottom-end"
        unstable_offset={[75, 0]}
        usePortal={true}
        title={
          <FormattedMessage
            id="cohort.cohortPicker.cohortsTitle"
            defaultMessage="Cohorts"
          />
        }
        anchorElement={
          <Button
            aria-label={
              <FormattedMessage
                id="cohort.cohortPicker.openCohortPicker"
                defaultMessage="Open cohort picker"
              />
            }
            color="inverse"
            variant="text"
            className={classes.cohortPickerCohortButton}
            trailingIcon={hasMenuItems && ChevronDown}
            {...getTestProps('sidebarnav-cohort-picker')}
          >
            {activeProjectCohort
              ? activeProjectCohort.name
              : formatMessage(messages.allCohorts)}
          </Button>
        }
      >
        {({ popover }) => (
          <React.Fragment>
            <PopoverList>
              {!isUserAccessRestrictedToCohorts && (
                <PopoverItem
                  className={classes.popoverItem}
                  noTextWrap={true}
                  clipText={true}
                  onClick={this.handleSelect(null, popover)}
                  text={formatMessage(messages.allCohorts)}
                  justify="space-between"
                  {...getTestProps('cohort-popover-item-all')}
                >
                  {activeProjectCohort === null && (
                    <CheckCircle className={classes.completeIcon} />
                  )}
                </PopoverItem>
              )}
              {hasMenuItems &&
                selectableProjectCohorts.map((cohort, i) => (
                  <PopoverItem
                    className={classes.popoverItem}
                    key={i}
                    noTextWrap={true}
                    clipText={true}
                    onClick={this.handleSelect(cohort.id, popover)}
                    text={cohort.name}
                    justify="space-between"
                    {...getTestProps(`cohort-popover-item-${cohort.id}`)}
                  >
                    <Pill label={cohort.resultCount} className={classes.pill} />
                    {activeProjectCohort?.id === cohort.id && (
                      <CheckCircle className={classes.completeIcon} />
                    )}
                  </PopoverItem>
                ))}
            </PopoverList>
            <PopoverActions>
              <Link to={urls.app.cohorts()} className={classes.seeAllLink}>
                <Button
                  variant="outlined"
                  onClick={this.handleSeeAllCohortsClick(popover)}
                  fullWidth
                  {...getTestProps('cohort-picker-see-all')}
                >
                  <FormattedMessage
                    id="cohort.cohortPicker.seeAll"
                    defaultMessage="See All"
                  />
                </Button>
              </Link>
            </PopoverActions>
          </React.Fragment>
        )}
      </Popover>
    );
  }
}

const mapStateToProps = state => ({
  activeProjectCohort:
    projectCohortPickerSelectors.selectActiveProjectCohort(state),
  projectCohorts:
    projectCohortPickerSelectors.selectProjectCohortPickerState(state),
  activeAccount: accountSelectors.selectActiveAccount(state),
  activeProject: projectPickerSelectors.selectActiveProject(state),
  policy: evaluatedPolicySelectors.selectPolicyForUser(state),
  features: featuresSelectors.selectFeatures(state),
  selectableProjectCohorts:
    projectCohortPickerSelectors.selectableCohorts(state),
  defaultProjectCohortSelection:
    projectCohortPickerSelectors.defaultCohortSelection(state),
  isInitializingProjectCohorts:
    projectCohortPickerSelectors.areCohortsInitializing(state),
  isUserAccessRestrictedToCohorts:
    projectCohortPickerSelectors.isUserAccessRestrictedToCohorts(state),
});

const mapDispatchToProps = {
  push,
  fetchProjectCohorts: projectCohortPickerActions.fetchProjectCohorts,
  setActiveProjectCohort: projectCohortPickerActions.setActiveProjectCohort,
};

export default compose(
  watchForProjectChange({ onlyMountWithProject: true }),
  injectIntl,
  withStyles(styles),
  withRouter,
  connect(mapStateToProps, mapDispatchToProps),
)(ProjectCohortPicker);
